/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  FormHelperText,
  Select,
  Box,
  Typography,
} from '@mui/material';
import { z } from 'zod';
import { notifyAPIClient } from '../../utils/toast';
import { apiGet, apiPut } from '../../utils/apiClient';
import { subtleFieldBg } from '../../utils/rowHighlight';
import { getDefaultClassCapacity } from '../../utils/classCapacity';
import {
  Response,
  Class,
  CourseLevel,
  Subtype,
} from '../../types/ClassTable.type';

type UpdateClassDialogProps = {
  open: boolean;
  handleClose: () => void;
  record: Class | null;
  subtypeId: number;
  fetchClasses: () => void;
  subtypes?: Subtype[];
};

type ClassInputData = {
  id?: number;
  name: string;
  classDate: string;
  startTime: string;
  endTime: string;
  level: string;
  subtypeId: number;
  maxStudents: number;
  status: string;
  note: string;
};

const UpdateClassInfoInput = z.object({
  id: z.number().optional(),
  name: z
    .string()
    .min(1, 'Please enter class name')
    .refine((val) => val.length === 0 || val.trim().length > 0, {
      message: 'Class name cannot be just whitespace',
    }),
  classDate: z
    .string({
      required_error: 'Please enter class date',
    })
    .date('Please enter a valid date'),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  level: z.string().optional(),
  subtypeId: z.number().int().min(1, 'Please select a class type'),
  maxStudents: z.number().int().min(1, 'Maximum students must be at least 1'),
  status: z.string(),
  note: z.string().optional(),
});
type UpdateClassInfoInputType = z.infer<typeof UpdateClassInfoInput>;
type FormErrors = {
  name?: string;
  classDate?: string;
  startTime?: string;
  endTime?: string;
  level?: string;
  subtypeId?: string;
  maxStudents?: string;
  status?: string;
  note?: string;
};

const UpdateClassDialog = ({
  open,
  handleClose,
  record,
  subtypeId,
  fetchClasses,
  subtypes,
}: UpdateClassDialogProps) => {
  const [formErrors, setFormErrors] = React.useState<FormErrors>({});
  const [_selectedSubtypeId, setSelectedSubtypeId] =
    React.useState<number>(subtypeId);
  const [levelOptions, setLevelOptions] = React.useState<CourseLevel[]>([]);
  const [classData, setClassData] = React.useState<ClassInputData>({
    id: record?.id,
    name: record?.name || '',
    classDate: record?.classDate
      ? (() => {
          const date = new Date(record.classDate);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        })()
      : new Date().toISOString().split('T')[0],
    startTime: record?.startTime || '07:00',
    endTime: record?.endTime || '08:00',
    level: record?.level || '',
    subtypeId: subtypeId,
    maxStudents: record?.maxStudents || 4,
    status: record?.status || 'ACTIVE',
    note: record?.note || '',
  });

  const fetchCourseLevels = async () => {
    const res = await apiGet<Response<CourseLevel>>('/course/get-all-levels');
    setLevelOptions(res.data);
  };

  // Helper function to get subtype name
  const getSelectedSubtypeName = (): string => {
    const subtype = subtypes?.find((st) => st.id === classData.subtypeId);
    return subtype?.name || '';
  };

  // Helper function to get day abbreviation
  const getDayAbbreviation = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const dayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    return dayNames[date.getDay()];
  };

  // Handle input changes with auto-naming
  const handleDataChange = (
    key: keyof ClassInputData,
    value: string | number,
  ) => {
    setClassData((prev) => {
      const updated = { ...prev, [key]: value };

      if (key === 'level' || key === 'subtypeId') {
        const selectedSubtype = subtypes?.find(
          (subtype) => subtype.id === updated.subtypeId,
        );
        updated.maxStudents = getDefaultClassCapacity(
          selectedSubtype?.name,
          updated.level,
        );
      }

      // Auto-update name when relevant fields change
      if (
        key === 'level' ||
        key === 'startTime' ||
        key === 'endTime' ||
        key === 'classDate' ||
        key === 'subtypeId'
      ) {
        const subtypeName =
          key === 'subtypeId'
            ? subtypes?.find((st) => st.id === (value as number))?.name || ''
            : getSelectedSubtypeName();
        const dayAbbr = getDayAbbreviation(updated.classDate || '');
        const namePrefix =
          updated.level && subtypeName
            ? `${subtypeName} ${updated.level}`
            : subtypeName;
        updated.name = namePrefix
          ? `[${namePrefix}] - ${dayAbbr} ${updated.startTime}-${updated.endTime}`
          : `${dayAbbr} ${updated.startTime}-${updated.endTime}`;
      }

      return updated;
    });
  };

  const handleConfirm = async (data: UpdateClassInfoInputType) => {
    try {
      if (record) {
        const updateRes = await apiPut<
          {
            status: number;
            message: string;
            data: Class;
          },
          UpdateClassInfoInputType
        >('/class/update', data);
        notifyAPIClient(updateRes.message);
      }

      fetchClasses();
      handleClose();
    } catch (error) {
      notifyAPIClient(
        (error as any).response?.data?.message ||
          'An unexpected error occurred while processing your request.',
      );
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      const data = UpdateClassInfoInput.parse({
        id: classData.id,
        name: classData.name,
        classDate: classData.classDate,
        startTime: classData.startTime,
        endTime: classData.endTime,
        level: classData.level,
        subtypeId: classData.subtypeId,
        maxStudents: classData.maxStudents,
        status: classData.status,
        note: classData.note,
      });

      setFormErrors({});
      handleConfirm(data);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: FormErrors = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof FormErrors;
          fieldErrors[field] = err.message;
        });
        setFormErrors(fieldErrors);
      } else {
        console.error(error);
        notifyAPIClient(
          'An unexpected error occurred while processing your request.',
        );
      }
    }
  };

  React.useEffect(() => {
    if (open) {
      fetchCourseLevels();
      // Reset form data when dialog opens
      setClassData({
        id: record?.id,
        name: record?.name || '',
        classDate: record?.classDate
          ? (() => {
              const date = new Date(record.classDate);
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            })()
          : new Date().toISOString().split('T')[0],
        startTime: record?.startTime || '07:00',
        endTime: record?.endTime || '08:00',
        level: record?.level || '',
        subtypeId: subtypeId,
        maxStudents: record?.maxStudents || 4,
        status: record?.status || 'ACTIVE',
        note: record?.note || '',
      });
      setSelectedSubtypeId(subtypeId);
    }
  }, [open, record, subtypeId]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={() => {
        handleClose();
        setFormErrors({});
      }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        {/* Header with gradient */}
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: '#fff',
            py: 3,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="700">
              Update Class Information
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Modify class details and settings
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            py: 3,
            px: 4,
            overflowY: 'auto',
            maxHeight: '70vh',
          }}
        >
          {/* Class Type */}
          {subtypes && subtypes.length > 0 && (
            <FormControl
              fullWidth
              sx={{
                mt: 4,
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            >
              <InputLabel className="required-field">Class Type</InputLabel>
              <Select
                value={classData.subtypeId}
                onChange={(e) =>
                  handleDataChange('subtypeId', e.target.value as number)
                }
                label="Class Type"
              >
                {subtypes.map((subtype) => (
                  <MenuItem key={subtype.id} value={subtype.id}>
                    {subtype.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Class Name */}
          <TextField
            fullWidth
            className="required-field"
            label="Class Name"
            value={classData.name}
            onChange={(e) => handleDataChange('name', e.target.value)}
            error={Boolean(formErrors.name)}
            helperText={formErrors.name || 'Auto-generated based on selections'}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: subtleFieldBg,
              },
            }}
          />

          {/* Class Date */}
          <TextField
            fullWidth
            className="required-field"
            label="Class Date"
            type="date"
            value={classData.classDate}
            onChange={(e) => handleDataChange('classDate', e.target.value)}
            error={Boolean(formErrors.classDate)}
            helperText={formErrors.classDate}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />

          {/* Time Row */}
          <Box display="flex" gap={2} mb={2}>
            <TextField
              fullWidth
              className="required-field"
              label="Start Time"
              type="time"
              value={classData.startTime}
              onChange={(e) => handleDataChange('startTime', e.target.value)}
              error={Boolean(formErrors.startTime)}
              helperText={formErrors.startTime}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
            <TextField
              fullWidth
              className="required-field"
              label="End Time"
              type="time"
              value={classData.endTime}
              onChange={(e) => handleDataChange('endTime', e.target.value)}
              error={Boolean(formErrors.endTime)}
              helperText={formErrors.endTime}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
          </Box>

          {/* Level */}
          {levelOptions.length > 0 && (
            <FormControl
              fullWidth
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            >
              <InputLabel>Level</InputLabel>
              <Select
                value={classData.level}
                onChange={(e) => handleDataChange('level', e.target.value)}
                label="Level"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {levelOptions.map((level, index) => (
                  <MenuItem key={index} value={level.levelName}>
                    {level.levelName}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.level && (
                <FormHelperText error>{formErrors.level}</FormHelperText>
              )}
            </FormControl>
          )}

          {/* Maximum students */}
          <TextField
            fullWidth
            label="Maximum Students"
            type="number"
            value={classData.maxStudents}
            onChange={(e) =>
              handleDataChange(
                'maxStudents',
                Math.max(1, Number(e.target.value) || 1),
              )
            }
            inputProps={{ min: 1, step: 1 }}
            helperText="Default is based on class type and level. Admin can customize it."
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />

          {/* Status */}
          <FormControl
            fullWidth
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          >
            <InputLabel className="required-field">Status</InputLabel>
            <Select
              value={classData.status}
              onChange={(e) => handleDataChange('status', e.target.value)}
              label="Status"
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </Select>
            {formErrors.status && (
              <FormHelperText error>{formErrors.status}</FormHelperText>
            )}
          </FormControl>

          {/* Note */}
          <TextField
            fullWidth
            label="Note"
            multiline
            rows={3}
            value={classData.note}
            onChange={(e) => handleDataChange('note', e.target.value)}
            error={Boolean(formErrors.note)}
            helperText={formErrors.note}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => {
              handleClose();
              setFormErrors({});
            }}
            sx={{
              px: 3,
              py: 1,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              color: 'white',
              px: 4,
              py: 1,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Update Class
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateClassDialog;
